import React, { useState, useEffect, useRef } from 'react';
import { BankStatementsTab } from './Finance';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── ACCOUNTING MODULE — Chart of Accounts (Phase 1) ─────────────────────────
// New module, separate from Finance (client billing) and Reports (read-only
// summaries) — see docs discussion: Accounting is where you enter/categorize,
// Reports is the only place anything gets displayed as a statement. Ledgers
// for Bank Accounts and Business/Client Expense Categories are kept in sync
// automatically by the backend (routes/accountingRoutes.js) — nothing here
// duplicates data entry that already exists elsewhere.
//
// Vouchers (Fixed Assets / Loans / Capital / Journal posting) and moving the
// Bank Statement workflow in from Finance are Phase 2 — this ships the Chart
// of Accounts (Groups + Ledgers) end to end first.

const NATURE_LABELS = { asset: 'Asset', liability: 'Liability', income: 'Income', expense: 'Expense', equity: 'Equity' };
const NATURE_COLORS = { asset: '#2563eb', liability: '#dc2626', income: '#16a34a', expense: '#c2410c', equity: '#7c3aed' };

// ── Shared small helpers (Statements/Ratios tabs) ───────────────────────────
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };

function StatCard({ label, value, color = '#3b82f6', icon, darkMode, T }) {
  return (
    <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '18px 20px', boxShadow: darkMode ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
        <span style={{ fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <p style={{ fontSize: 22, fontWeight: 800, color, margin: 0 }}>{value}</p>
    </div>
  );
}

function Section({ title, subtitle, children, T, darkMode, actions }) {
  return (
    <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: 'hidden', boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.25)' : '0 2px 12px rgba(15,31,69,0.06)' }}>
      <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>{title}</p>
          {subtitle && <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>{subtitle}</p>}
        </div>
        {actions}
      </div>
      <div style={{ padding: 22 }}>{children}</div>
    </div>
  );
}

function ReportTable({ cols, rows, emptyMsg = 'No data found', T, darkMode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
          <tr>{cols.map((c, i) => (
            <th key={i} style={{ padding: '10px 16px', textAlign: c.right ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{c.label}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} style={{ padding: '48px 16px', textAlign: 'center', color: T.text3, fontSize: 13 }}>{emptyMsg}</td></tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff') }}>
              {cols.map((c, j) => (
                <td key={j} style={{ padding: '11px 16px', fontSize: 13, color: T.text2, textAlign: c.right ? 'right' : 'left', fontWeight: c.bold ? 600 : 400, whiteSpace: c.nowrap ? 'nowrap' : 'normal' }}>
                  {c.render ? c.render(row) : row[c.key] ?? '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DateFilter({ from, to, onFrom, onTo, onApply, loading, T, darkMode }) {
  const presets = [
    { label: 'This Month', fn: () => { onFrom(monthStart()); onTo(today()); } },
    { label: 'Last 3 Months', fn: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); onFrom(d.toISOString().split('T')[0]); onTo(today()); } },
    { label: 'This Year', fn: () => { onFrom(`${new Date().getFullYear()}-01-01`); onTo(today()); } },
  ];
  const is = { border: `1px solid ${T.inputBorder}`, borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '14px 18px', background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 4 }}>
      {presets.map(p => (
        <button key={p.label} onClick={p.fn} style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>{p.label}</button>
      ))}
      <div style={{ width: 1, height: 20, background: T.border, margin: '0 4px' }} />
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} style={is} />
      <span style={{ fontSize: 12, color: T.text3 }}>to</span>
      <input type="date" value={to} onChange={e => onTo(e.target.value)} style={is} />
      <button onClick={onApply} disabled={loading} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>Apply</button>
    </div>
  );
}

// Small icon buttons shared by Group rows and Ledger rows — pencil (edit)
// and trash (delete). `disabled` greys the button out with a title tooltip
// instead of hiding it, so a default/locked row still visibly HAS the
// controls (per the request that every row show Edit/Delete), it just
// explains why they won't work rather than silently vanishing.
function IconBtn({ kind, onClick, disabled, title, danger }) {
  const color = disabled ? '#cbd5e1' : (danger ? '#ef4444' : '#475569');
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled} title={title}
      style={{ width:24, height:24, borderRadius:6, border:'none', background:'none', color, cursor: disabled ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      {kind === 'edit' ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z"/></svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
      )}
    </button>
  );
}

// Every id in candidateId's own subtree (candidateId included) — used to
// keep a Group's "Under" picker from offering itself or one of its own
// descendants as its new parent, which would create a cycle.
function subtreeIds(groups, rootId) {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of groups) {
      if (ids.has(g.parent_group_id) && !ids.has(g.id)) { ids.add(g.id); grew = true; }
    }
  }
  return ids;
}

// Flattened, indented options for a Group picker — depth-first so children
// always follow their parent, matching how Tally's own "List of Groups"
// reads. `excludeIds` drops a group (and, from the caller, its own subtree)
// out of the list — used when editing a Group so it can't be reparented
// under itself or a descendant.
function groupOptions(groups, excludeIds = new Set()) {
  const byParent = new Map();
  for (const g of groups) {
    if (excludeIds.has(g.id)) continue;
    const key = g.parent_group_id || 'root';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(g);
  }
  const out = [];
  const walk = (parentKey, depth) => {
    for (const g of (byParent.get(parentKey) || []).sort((a, b) => a.name.localeCompare(b.name))) {
      out.push({ ...g, depth });
      walk(g.id, depth + 1);
    }
  };
  walk('root', 0);
  return out;
}

// Professional custom "Under Group" picker — same search-filterable
// floating-panel pattern used across the app for master pickers (see
// TypeDropdown/CategoryDropdown in Clients.js/Tasks.js), replacing the
// browser's native <select> (which renders with plain OS chrome and can't
// show hierarchy depth or the nature badge). `options` is the output of
// groupOptions() above, so depth/indentation is already computed.
function GroupSelect({ options, value, onChange, darkMode = false, T: theme, placeholder = 'Select group...', disabled = false }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(o => String(o.id) === String(value));
  const filtered = search.trim() ? options.filter(o => o.name.toLowerCase().includes(search.trim().toLowerCase())) : options;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
          border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13,
          background: disabled ? T.bg3 : T.inputBg, color: selected ? (disabled ? T.text3 : T.text) : T.text3,
          cursor: disabled ? 'not-allowed' : 'pointer', boxSizing:'border-box', textAlign:'left', transition:'border-color 0.15s',
        }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {selected ? '— '.repeat(selected.depth || 0) + selected.name : placeholder}
        </span>
        {selected && <span style={{ flexShrink:0, fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: disabled ? 'transparent' : (darkMode?'rgba(255,255,255,0.08)':'#f1f5f9'), color: NATURE_COLORS[selected.nature] }}>{NATURE_LABELS[selected.nature]}</span>}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && !disabled && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
            <input autoFocus type="text" value={search} placeholder="Search groups..."
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
          </div>
          <div style={{ maxHeight:240, overflowY:'auto', padding:5 }}>
            {filtered.map(o => {
              const sel = String(o.id) === String(value);
              return (
                <button key={o.id} type="button" onClick={() => { onChange(String(o.id)); setOpen(false); setSearch(''); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:`8px 10px 8px ${10 + (o.depth || 0) * 14}px`, borderRadius:8, border:'none', cursor:'pointer', fontSize:13, textAlign:'left', fontWeight: sel ? 700 : 500, color: sel ? '#2563eb' : T.text, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {o.depth > 0 && <span style={{ color:T.text3 }}>{'— '.repeat(o.depth)}</span>}
                    {o.name}
                  </span>
                  <span style={{ flexShrink:0, fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: sel ? 'rgba(37,99,235,0.12)' : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'), color: sel ? '#2563eb' : T.text3 }}>{NATURE_LABELS[o.nature]}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>{options.length === 0 ? 'No groups available' : `No match for "${search}"`}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Professional custom Ledger picker — same searchable floating-panel
// pattern as GroupSelect above, used everywhere a voucher line needs a
// Ledger (replaces the plain native <select> that used to dump every
// Ledger in one flat browser list). Each row shows which Group the Ledger
// belongs to, colored by that Group's nature, so picking the right one
// among many doesn't require memorizing the Chart of Accounts by heart.
function LedgerSelect({ ledgers, value, onChange, darkMode = false, T: theme, placeholder = 'Select ledger...' }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sorted = [...ledgers].sort((a, b) => a.name.localeCompare(b.name));
  const selected = sorted.find(l => String(l.id) === String(value));
  const filtered = search.trim()
    ? sorted.filter(l => l.name.toLowerCase().includes(search.trim().toLowerCase()) || l.group_name?.toLowerCase().includes(search.trim().toLowerCase()))
    : sorted;

  return (
    <div ref={ref} style={{ position:'relative', flex:1, minWidth:0 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
          border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13,
          background:T.inputBg, color: selected ? T.text : T.text3, cursor:'pointer', boxSizing:'border-box',
          textAlign:'left', transition:'border-color 0.15s',
        }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selected ? selected.name : placeholder}</span>
        <span style={{ flexShrink:0, display:'flex', alignItems:'center', gap:6 }}>
          {selected && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color: NATURE_COLORS[selected.group_nature] }}>{selected.group_name}</span>}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
        </span>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div style={{ padding:8, borderBottom:`1px solid ${T.border}` }}>
            <input autoFocus type="text" value={search} placeholder="Search ledgers..."
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12.5, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
          </div>
          <div style={{ maxHeight:260, overflowY:'auto', padding:5 }}>
            {filtered.map(l => {
              const sel = String(l.id) === String(value);
              return (
                <button key={l.id} type="button" onClick={() => { onChange(String(l.id)); setOpen(false); setSearch(''); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, textAlign:'left', fontWeight: sel ? 700 : 500, color: sel ? '#2563eb' : T.text, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.name}</span>
                  <span style={{ flexShrink:0, fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: sel ? 'rgba(37,99,235,0.12)' : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'), color: sel ? '#2563eb' : NATURE_COLORS[l.group_nature] }}>{l.group_name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="3" style={{ flexShrink:0 }}><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>{ledgers.length === 0 ? 'No ledgers available' : `No match for "${search}"`}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GroupModal({ token, groups, group, defaultParentId, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const isEdit = !!group;
  const [form, setForm] = useState({ name: group?.name || '', parent_group_id: group?.parent_group_id || defaultParentId || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;

  const excludeIds = isEdit ? subtreeIds(groups, group.id) : new Set();
  const options = groupOptions(groups, excludeIds);

  const handleSave = async () => {
    setError('');
    if (!form.name.trim()) { setError('Group name is required.'); return; }
    if (!form.parent_group_id) { setError('Select a Group to nest this Under.'); return; }
    setSaving(true);
    try {
      const url = isEdit ? `${BASE}/api/accounting/groups/${group.id}` : `${BASE}/api/accounting/groups`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Could not save Group'); setSaving(false); return; }
      onSuccess(d);
    } catch (e) { setError('Server error'); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,440px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>{isEdit ? 'Edit Group' : 'Create Group'}</h3>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14 }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}
          <div>
            <SL req>Name</SL>
            <input type="text" placeholder="e.g. Sundry Creditors" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={is} />
          </div>
          <div>
            <SL req>Under</SL>
            <GroupSelect options={options} value={form.parent_group_id} onChange={v=>setForm(f=>({...f,parent_group_id:v}))} darkMode={darkMode} T={T} />
          </div>
          <p style={{ fontSize:11, color:T.text3, margin:0, lineHeight:1.5 }}>A Group takes on the nature (Asset / Liability / Income / Expense / Equity) of whatever it's nested Under — same as Tally.</p>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving...' : 'Save Group'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LedgerModal({ token, groups, ledger, defaultGroupId, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const isEdit = !!ledger;
  // A Business Expense Category ledger is the one system ledger whose Name
  // is genuinely editable here — it's the same record now, not a mirror
  // (see accountingRoutes.js PUT /ledgers/:id). Its Group still can't move
  // (a category is always under Indirect Expenses). Every other system
  // ledger (Bank Account, Client Expense Category, Cash, Sundry Debtors,
  // Sales, Output GST) stays fully locked — Name AND Group.
  const isBizCategory = isEdit && ledger.is_system && ledger.source_type === 'business_expense_category';
  const locked = isEdit && ledger.is_system && !isBizCategory;
  const nameLocked = locked;
  const groupLocked = locked || isBizCategory;
  const [form, setForm] = useState({ name: ledger?.name || '', group_id: ledger?.group_id || defaultGroupId || '', opening_balance: ledger ? String(ledger.opening_balance ?? 0) : '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const is = (dis) => ({ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background: dis ? T.bg3 : T.inputBg, color: dis ? T.text3 : T.text, boxSizing:'border-box' });
  const SL = ({ children, req }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>{children}{req && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</label>;
  const options = groupOptions(groups);

  const handleSave = async () => {
    setError('');
    if (!nameLocked && !form.name.trim()) { setError('Ledger name is required.'); return; }
    if (!groupLocked && !form.group_id) { setError('Select a Group.'); return; }
    setSaving(true);
    try {
      const url = isEdit ? `${BASE}/api/accounting/ledgers/${ledger.id}` : `${BASE}/api/accounting/ledgers`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Could not save ledger'); setSaving(false); return; }
      onSuccess(d);
    } catch (e) { setError('Server error'); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,440px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'18px 18px 0 0' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>{isEdit ? 'Edit Ledger' : 'Add Ledger'}</h3>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:14 }}>
          {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}
          {locked && <div style={{ padding:'8px 12px', borderRadius:8, background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', color:T.text3, fontSize:11.5, lineHeight:1.5 }}>This Ledger mirrors a Bank Account or Category — its Name and Group are managed from there. Only Opening Balance can be changed here.</div>}
          {isBizCategory && <div style={{ padding:'8px 12px', borderRadius:8, background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', color:T.text3, fontSize:11.5, lineHeight:1.5 }}>This is a Business Expense Category — renaming it here updates it everywhere it's used. It always stays under Indirect Expenses.</div>}
          <div>
            <SL req>Ledger Name</SL>
            <input disabled={nameLocked} type="text" placeholder="e.g. Furniture &amp; Fixtures" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={is(nameLocked)} />
          </div>
          <div>
            <SL req>Under Group</SL>
            <GroupSelect options={options} value={form.group_id} onChange={v=>setForm(f=>({...f,group_id:v}))} disabled={groupLocked} darkMode={darkMode} T={T} />
          </div>
          <div>
            <SL>Opening Balance (₹)</SL>
            <input type="number" step="0.01" placeholder="0.00" value={form.opening_balance} onChange={e=>setForm(f=>({...f,opening_balance:e.target.value}))} style={is(false)} />
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: saving?'#94a3b8':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving...' : 'Save Ledger'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shared confirm/alert popups — replace window.confirm/window.alert
// everywhere in this module with something styled consistently with the
// rest of the app, instead of the browser's own unstyled dialog.
function ConfirmPopup({ title = 'Are you sure?', message, confirmLabel = 'Delete', danger = true, onConfirm, onCancel, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,420px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ padding:'24px 24px 18px' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:T.text, margin:'0 0 8px' }}>{title}</h3>
          <p style={{ fontSize:13, color:T.text2, margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onCancel} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: danger ? '#dc2626' : 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function AlertPopup({ title = 'Notice', message, onClose, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', border:'#e8edf5', text:'#0f1f45', text2:'#4a5568' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:18, width:'min(92vw,420px)', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ padding:'24px 24px 18px' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:T.text, margin:'0 0 8px' }}>{title}</h3>
          <p style={{ fontSize:13, color:T.text2, margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', padding:'16px 24px', borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>OK</button>
        </div>
      </div>
    </div>
  );
}

function ChartOfAccountsTab({ token, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [ledgers, setLedgers] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingLedgers, setLoadingLedgers] = useState(true);
  const [ledgerModal, setLedgerModal] = useState(null); // { mode:'add'|'edit', ledger? }
  const [groupModal, setGroupModal] = useState(null);   // { mode:'add'|'edit', group? }
  const [confirmState, setConfirmState] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const fetchGroups = () => {
    setLoadingGroups(true);
    fetch(`${BASE}/api/accounting/groups`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => {
        const list = Array.isArray(d) ? d : [];
        setGroups(list);
        setSelectedGroupId(prev => prev || (list[0] ? String(list[0].id) : ''));
        setLoadingGroups(false);
      }).catch(() => setLoadingGroups(false));
  };

  useEffect(() => { fetchGroups(); }, [token]); // eslint-disable-line

  const fetchLedgers = () => {
    if (!selectedGroupId) { setLedgers([]); setLoadingLedgers(false); return; }
    setLoadingLedgers(true);
    fetch(`${BASE}/api/accounting/ledgers?group_id=${selectedGroupId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setLedgers(Array.isArray(d) ? d : []); setLoadingLedgers(false); }).catch(() => setLoadingLedgers(false));
  };

  useEffect(() => { fetchLedgers(); }, [selectedGroupId]); // eslint-disable-line

  const handleDeleteLedger = (ledger) => {
    setConfirmState({
      title: 'Delete Ledger', message: `Delete "${ledger.name}"? This can't be undone.`,
      onConfirm: async () => {
        setConfirmState(null);
        const res = await fetch(`${BASE}/api/accounting/ledgers/${ledger.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) { const d = await res.json().catch(() => ({})); setAlertMsg(d.message || 'Could not delete'); return; }
        fetchLedgers();
      },
    });
  };

  const handleDeleteGroup = (g) => {
    setConfirmState({
      title: 'Delete Group', message: `Delete Group "${g.name}"? This can't be undone.`,
      onConfirm: async () => {
        setConfirmState(null);
        const res = await fetch(`${BASE}/api/accounting/groups/${g.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) { const d = await res.json().catch(() => ({})); setAlertMsg(d.message || 'Could not delete'); return; }
        if (String(selectedGroupId) === String(g.id)) setSelectedGroupId('');
        fetchGroups();
      },
    });
  };

  // Recursive tree (a custom Group can now nest under any other Group, not
  // just the fixed two levels STANDARD_GROUPS ships with).
  const childrenOf = (id) => groups.filter(g => g.parent_group_id === id).sort((a, b) => a.name.localeCompare(b.name));
  const topGroups = groups.filter(g => !g.parent_group_id);
  const selectedGroup = groups.find(g => String(g.id) === String(selectedGroupId));

  const GroupRow = ({ g, depth }) => {
    const active = String(g.id) === String(selectedGroupId);
    const kids = childrenOf(g.id);
    return (
      <div>
        <div style={{
          display:'flex', alignItems:'center', gap:2, borderRadius:8,
          background: active ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
        }}>
          <button onClick={() => setSelectedGroupId(String(g.id))} style={{
            flex:1, minWidth:0, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between',
            padding: depth ? `8px 6px 8px ${12 + depth * 14}px` : '9px 10px', border:'none', background:'none', cursor:'pointer',
            color: active ? '#fff' : '#475569', fontSize:12.5, fontWeight: active ? 700 : 600,
          }}>
            <span style={{ display:'flex', alignItems:'center', gap:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {g.name}
              {g.is_system && !depth && ['Bank Accounts','Indirect Expenses','Direct Expenses'].includes(g.name) && (
                <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, flexShrink:0, background: active ? 'rgba(255,255,255,0.18)' : (g.name==='Direct Expenses' ? '#eff6ff' : '#fff7ed'), color: active ? '#fff' : (g.name==='Direct Expenses' ? '#2563eb' : '#c2410c') }}>
                  {g.name === 'Bank Accounts' ? 'auto' : g.name === 'Direct Expenses' ? 'from Client Exp.' : 'from Business Exp.'}
                </span>
              )}
              {!g.is_system && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, flexShrink:0, background: active ? 'rgba(255,255,255,0.18)' : '#f0fdf4', color: active ? '#fff' : '#16a34a' }}>custom</span>}
            </span>
            <span style={{ color: active ? 'rgba(255,255,255,0.7)' : '#cbd5e1', fontSize:11, flexShrink:0, marginLeft:6 }}>{g.ledger_count}</span>
          </button>
          {!g.is_system && (
            <div style={{ display:'flex', flexShrink:0, paddingRight:4 }}>
              <IconBtn kind="edit" title="Edit Group" onClick={() => setGroupModal({ mode:'edit', group: g })} />
              <IconBtn kind="delete" danger title={g.ledger_count > 0 ? 'Move or delete its Ledgers first' : g.child_count > 0 ? 'Move or delete its sub-Groups first' : 'Delete Group'}
                disabled={Number(g.ledger_count) > 0 || Number(g.child_count) > 0} onClick={() => handleDeleteGroup(g)} />
            </div>
          )}
        </div>
        {kids.map(child => <GroupRow key={child.id} g={child} depth={(depth || 0) + 1} />)}
      </div>
    );
  };

  return (
    <div style={{ display:'flex', gap:18, minHeight:0, flexGrow:1 }}>
      {ledgerModal && (
        <LedgerModal token={token} groups={groups} ledger={ledgerModal.ledger} defaultGroupId={selectedGroupId}
          onClose={() => setLedgerModal(null)}
          onSuccess={() => { setLedgerModal(null); fetchGroups(); fetchLedgers(); }}
          darkMode={darkMode} T={T} />
      )}
      {groupModal && (
        <GroupModal token={token} groups={groups} group={groupModal.group} defaultParentId={selectedGroupId}
          onClose={() => setGroupModal(null)}
          onSuccess={() => { setGroupModal(null); fetchGroups(); }}
          darkMode={darkMode} T={T} />
      )}
      {confirmState && (
        <ConfirmPopup title={confirmState.title} message={confirmState.message}
          onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)}
          darkMode={darkMode} T={T} />
      )}
      {alertMsg && <AlertPopup message={alertMsg} onClose={() => setAlertMsg('')} darkMode={darkMode} T={T} />}

      <div style={{ width:300, flexShrink:0, background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em' }}>Groups</div>
          <button onClick={() => setGroupModal({ mode:'add' })} title="Create Group" style={{ width:24, height:24, borderRadius:7, border:'none', background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color:T.text2, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
        <div style={{ padding:8, display:'flex', flexDirection:'column', gap:2, overflowY:'auto' }}>
          {loadingGroups ? (
            <p style={{ fontSize:12, color:T.text3, padding:'12px 10px' }}>Loading...</p>
          ) : topGroups.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(g => <GroupRow key={g.id} g={g} />)}
        </div>
      </div>

      <div style={{ flexGrow:1, background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'16px 22px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:T.text, display:'flex', alignItems:'center', gap:8 }}>
              {selectedGroup?.name || '—'}
              {selectedGroup && <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color: NATURE_COLORS[selectedGroup.nature] }}>{NATURE_LABELS[selectedGroup.nature]}</span>}
            </div>
            <div style={{ fontSize:11.5, color:T.text3, marginTop:2 }}>{ledgers.length} ledger{ledgers.length===1?'':'s'}</div>
          </div>
          <button onClick={() => setLedgerModal({ mode:'add' })} style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Ledger
          </button>
        </div>

        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              {['Ledger','File Number','Opening Balance','Current Balance','Actions'].map((h,i) => (
                <th key={h} style={{ padding:'11px 22px', textAlign: i===0||i===1?'left':'right', fontSize:10.5, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loadingLedgers ? (
              <tr><td colSpan={5} style={{ padding:'40px', textAlign:'center', color:T.text3, fontSize:13 }}>Loading...</td></tr>
            ) : ledgers.length === 0 ? (
              <tr><td colSpan={5} style={{ padding:'40px', textAlign:'center', color:T.text3, fontSize:13 }}>No ledgers under this group yet. Click Add Ledger to create one.</td></tr>
            ) : ledgers.map((l, i) => (
              <tr key={l.id} style={{ borderBottom:`1px solid ${T.border}`, background: i%2===0 ? T.bg2 : T.bg3 }}>
                <td style={{ padding:'13px 22px', fontSize:13, fontWeight:700, color:T.text }}>
                  {l.name}
                  {l.is_system && <span style={{ marginLeft:8, fontSize:9.5, fontWeight:700, padding:'1px 7px', borderRadius:8, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color:T.text3 }}>auto</span>}
                </td>
                <td style={{ padding:'13px 22px', fontSize:12.5, color:T.text2 }}>{l.file_number || ''}</td>
                <td style={{ padding:'13px 22px', textAlign:'right', fontSize:12.5, color:T.text2, fontVariantNumeric:'tabular-nums' }}>{fc(l.opening_balance)}</td>
                <td style={{ padding:'13px 22px', textAlign:'right', fontSize:13, fontWeight:700, color:T.text, fontVariantNumeric:'tabular-nums' }}>{fc(l.current_balance ?? l.opening_balance)}</td>
                <td style={{ padding:'13px 22px' }}>
                  <div style={{ display:'flex', justifyContent:'flex-end', gap:2 }}>
                    <IconBtn kind="edit" title="Edit Ledger" onClick={() => setLedgerModal({ mode:'edit', ledger: l })} />
                    <IconBtn kind="delete" danger
                      title={
                        l.is_system && l.source_type !== 'business_expense_category' ? 'Mirrors a Bank Account or Category — remove it from there instead' :
                        (Number(l.total_dr) > 0 || Number(l.total_cr) > 0) ? 'Has posted entries — cannot be deleted' : 'Delete Ledger'
                      }
                      disabled={(l.is_system && l.source_type !== 'business_expense_category') || Number(l.total_dr) > 0 || Number(l.total_cr) > 0}
                      onClick={() => handleDeleteLedger(l)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ margin:'18px 22px', padding:'12px 16px', borderRadius:10, background:'#eff6ff', border:'1px solid #dbeafe', fontSize:11.5, color:'#1e40af', lineHeight:1.6 }}>
          Bank Accounts and every Business/Client Expense category are created here automatically — nothing to re-type. Current Balance is Opening Balance walked forward by every posted Voucher — Receipts, Business/Client Expenses (with a Bank Account selected) and manual entries under the Vouchers tab all post here automatically. A Ledger or Group with entries posted against it can't be deleted — its Delete button greys out and explains why.
        </div>
      </div>
    </div>
  );
}

const VOUCHER_TYPES = [
  { key: 'payment',        label: 'Payment' },
  { key: 'receipt',        label: 'Receipt' },
  { key: 'contra',         label: 'Contra', hint: 'bank ↔ cash / bank ↔ bank' },
  { key: 'purchase_asset', label: 'Purchase / Fixed Asset' },
  { key: 'loan',           label: 'Loan Given / Taken' },
  { key: 'capital',        label: 'Capital Introduced / Withdrawn' },
  { key: 'journal',        label: 'Journal', hint: 'manual adjustment' },
];
const VOUCHER_TYPE_LABEL = Object.fromEntries(VOUCHER_TYPES.map(v => [v.key, v.label]));
// A Payment always moves money OUT of a Bank/Cash ledger and a Receipt
// always moves it IN — so for these two types the "other side" of the
// entry is never itself a Bank/Cash ledger, and the Bank/Cash side should
// never be forced to hunt through the full Chart of Accounts to find the
// handful of ledgers that actually apply. Contra (bank↔cash/bank↔bank) is
// deliberately NOT restricted this way — both its sides genuinely are
// Bank/Cash ledgers.
const BANK_CASH_GROUP_NAMES = ['Bank Accounts', 'Cash-in-Hand'];

function VouchersTab({ token, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const fd = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const [voucherType, setVoucherType] = useState('purchase_asset');
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().split('T')[0]);
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState([{ ledger_id: '', type: 'dr', amount: '' }, { ledger_id: '', type: 'cr', amount: '' }]);
  // Payment/Receipt use this simpler two-ledger form instead of the generic
  // Dr/Cr line builder below — one side is always the "other" ledger
  // (expense, income, debtor, whatever), the other is always Bank/Cash.
  const [simpleOtherLedgerId, setSimpleOtherLedgerId] = useState('');
  const [simpleBankLedgerId, setSimpleBankLedgerId] = useState('');
  const [simpleAmount, setSimpleAmount] = useState('');
  const [ledgers, setLedgers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [vouchers, setVouchers] = useState([]);
  const [loadingVouchers, setLoadingVouchers] = useState(true);
  const [confirmState, setConfirmState] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  const isSimple = voucherType === 'payment' || voucherType === 'receipt';
  const bankCashLedgers = ledgers.filter(l => BANK_CASH_GROUP_NAMES.includes(l.group_name));

  useEffect(() => {
    fetch(`${BASE}/api/accounting/ledgers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLedgers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const fetchVouchers = () => {
    setLoadingVouchers(true);
    fetch(`${BASE}/api/accounting/vouchers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setVouchers(Array.isArray(d) ? d : []); setLoadingVouchers(false); }).catch(() => setLoadingVouchers(false));
  };
  useEffect(() => { fetchVouchers(); }, [token]); // eslint-disable-line

  // Switching voucher type clears whichever form isn't shown anymore, so a
  // leftover selection from Payment can't silently ride along into a
  // Journal entry (or vice versa).
  useEffect(() => {
    setSimpleOtherLedgerId(''); setSimpleBankLedgerId(''); setSimpleAmount('');
    setLines([{ ledger_id: '', type: 'dr', amount: '' }, { ledger_id: '', type: 'cr', amount: '' }]);
    setError('');
  }, [voucherType]);

  const updateLine = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(prev => [...prev, { ledger_id: '', type: 'dr', amount: '' }]);
  const removeLine = (i) => setLines(prev => prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev);

  const simpleAmountNum = Number(simpleAmount) || 0;
  const totalDr = isSimple ? simpleAmountNum : lines.filter(l => l.type === 'dr').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const totalCr = isSimple ? simpleAmountNum : lines.filter(l => l.type === 'cr').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const balanced = isSimple
    ? simpleAmountNum > 0 && !!simpleOtherLedgerId && !!simpleBankLedgerId
    : totalDr > 0 && Math.abs(totalDr - totalCr) < 0.01;

  const handleSave = async () => {
    setError('');
    let validLines;
    if (isSimple) {
      if (!simpleOtherLedgerId || !simpleBankLedgerId) { setError(voucherType === 'payment' ? 'Select both the Expense/Payee Ledger and the Bank/Cash Ledger.' : 'Select both the Bank/Cash Ledger and the Ledger being received against.'); return; }
      if (simpleAmountNum <= 0) { setError('Enter an amount.'); return; }
      validLines = voucherType === 'payment'
        ? [{ ledger_id: simpleOtherLedgerId, type: 'dr', amount: simpleAmountNum }, { ledger_id: simpleBankLedgerId, type: 'cr', amount: simpleAmountNum }]
        : [{ ledger_id: simpleBankLedgerId, type: 'dr', amount: simpleAmountNum }, { ledger_id: simpleOtherLedgerId, type: 'cr', amount: simpleAmountNum }];
    } else {
      validLines = lines.filter(l => l.ledger_id && Number(l.amount) > 0);
      if (validLines.length < 2) { setError('Add at least one Dr line and one Cr line.'); return; }
      if (!balanced) { setError(`Not balanced — Total Dr ${fc(totalDr)} ≠ Total Cr ${fc(totalCr)}`); return; }
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/vouchers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ voucher_type: voucherType, voucher_date: voucherDate, narration, lines: validLines }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Could not save voucher'); setSaving(false); return; }
      setSimpleOtherLedgerId(''); setSimpleBankLedgerId(''); setSimpleAmount('');
      setLines([{ ledger_id: '', type: 'dr', amount: '' }, { ledger_id: '', type: 'cr', amount: '' }]);
      setNarration('');
      fetchVouchers();
    } catch (e) { setError('Server error'); }
    setSaving(false);
  };

  const handleDelete = (v) => {
    if (v.linked_type) return;
    setConfirmState({
      title: 'Delete Voucher', message: `Delete this ${VOUCHER_TYPE_LABEL[v.voucher_type] || 'voucher'}${v.voucher_number ? ` (${v.voucher_number})` : ''}? This can't be undone.`,
      onConfirm: async () => {
        setConfirmState(null);
        const res = await fetch(`${BASE}/api/accounting/vouchers/${v.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) { const d = await res.json().catch(() => ({})); setAlertMsg(d.message || 'Could not delete'); return; }
        fetchVouchers();
      },
    });
  };

  return (
    <div style={{ display:'flex', gap:18, minHeight:0, flexGrow:1 }}>
      {confirmState && (
        <ConfirmPopup title={confirmState.title} message={confirmState.message}
          onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)}
          darkMode={darkMode} T={T} />
      )}
      {alertMsg && <AlertPopup message={alertMsg} onClose={() => setAlertMsg('')} darkMode={darkMode} T={T} />}
      <div style={{ width:280, flexShrink:0, display:'flex', flexDirection:'column', gap:8 }}>
        <div style={{ fontSize:11, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', padding:'0 4px' }}>New Voucher</div>
        {VOUCHER_TYPES.map(v => (
          <button key={v.key} onClick={() => setVoucherType(v.key)} style={{
            padding:'12px 14px', borderRadius:11, textAlign:'left', cursor:'pointer',
            border: voucherType===v.key ? 'none' : `1px solid ${T.border2}`,
            background: voucherType===v.key ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2,
            color: voucherType===v.key ? '#fff' : T.text2,
            fontSize:12.5, fontWeight: voucherType===v.key ? 700 : 600,
            boxShadow: voucherType===v.key ? '0 4px 14px rgba(15,31,69,0.25)' : 'none',
          }}>
            {v.label}{v.hint && <span style={{ fontSize:10.5, fontWeight:500, opacity:0.75 }}> ({v.hint})</span>}
          </button>
        ))}
        <div style={{ marginTop:6, padding:'14px 16px', borderRadius:12, background:'#eff6ff', border:'1px solid #dbeafe', fontSize:11.5, color:'#1e40af', lineHeight:1.65 }}>
          Receipts, Business Expenses and Client Expenses already post their own voucher automatically when saved from their usual screens (with a Bank Account selected) — this form only covers what those don't.
        </div>
      </div>

      <div style={{ flexGrow:1, display:'flex', flexDirection:'column', gap:18, minHeight:0 }}>
        {/* overflow:visible (not the usual :hidden for a rounded card) — this one holds LedgerSelect's floating dropdown, which needs to render past this card's own edge. Nothing in here has a background that would otherwise bleed past the rounded corners, so this is safe unlike the table cards below. */}
        <div style={{ background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'visible', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)' }}>
          <div style={{ padding:'16px 24px', borderBottom:`1px solid ${T.border}`, borderTopLeftRadius:16, borderTopRightRadius:16 }}>
            <div style={{ fontSize:15, fontWeight:800, color:T.text }}>{VOUCHER_TYPE_LABEL[voucherType]} Voucher</div>
          </div>
          <div style={{ padding:'22px 24px', display:'flex', flexDirection:'column', gap:18 }}>
            {error && <div style={{ padding:'8px 12px', borderRadius:8, background:'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600 }}>{error}</div>}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:16 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Date <span style={{ color:'#ef4444' }}>*</span></div>
                <input type="date" value={voucherDate} onChange={e=>setVoucherDate(e.target.value)} style={is} />
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Narration</div>
                <input type="text" placeholder="What this voucher is for" value={narration} onChange={e=>setNarration(e.target.value)} style={is} />
              </div>
            </div>

            {isSimple ? (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Ledger entries</div>
                {/* No overflow:hidden here — LedgerSelect's floating dropdown panel needs to render outside this box's bounds when a row is near the bottom. */}
                <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:'visible' }}>
                  {voucherType === 'payment' ? (
                    <>
                      <div style={{ display:'flex', gap:10, alignItems:'center', padding:'12px', background:T.bg2, borderTopLeftRadius:9, borderTopRightRadius:9 }}>
                        <span style={{ flexShrink:0, width:64, fontSize:11.5, fontWeight:700, color:'#2563eb' }}>Dr</span>
                        <LedgerSelect ledgers={ledgers} value={simpleOtherLedgerId} onChange={setSimpleOtherLedgerId} placeholder="Select Expense / Payee ledger..." darkMode={darkMode} T={T} />
                      </div>
                      <div style={{ display:'flex', gap:10, alignItems:'center', padding:'12px', borderTop:`1px solid ${T.border}`, background:T.bg3, borderBottomLeftRadius:9, borderBottomRightRadius:9 }}>
                        <span style={{ flexShrink:0, width:64, fontSize:11.5, fontWeight:700, color:'#dc2626' }}>Cr</span>
                        <LedgerSelect ledgers={bankCashLedgers} value={simpleBankLedgerId} onChange={setSimpleBankLedgerId} placeholder="Select Bank / Cash ledger..." darkMode={darkMode} T={T} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display:'flex', gap:10, alignItems:'center', padding:'12px', background:T.bg2, borderTopLeftRadius:9, borderTopRightRadius:9 }}>
                        <span style={{ flexShrink:0, width:64, fontSize:11.5, fontWeight:700, color:'#2563eb' }}>Dr</span>
                        <LedgerSelect ledgers={bankCashLedgers} value={simpleBankLedgerId} onChange={setSimpleBankLedgerId} placeholder="Select Bank / Cash ledger..." darkMode={darkMode} T={T} />
                      </div>
                      <div style={{ display:'flex', gap:10, alignItems:'center', padding:'12px', borderTop:`1px solid ${T.border}`, background:T.bg3, borderBottomLeftRadius:9, borderBottomRightRadius:9 }}>
                        <span style={{ flexShrink:0, width:64, fontSize:11.5, fontWeight:700, color:'#dc2626' }}>Cr</span>
                        <LedgerSelect ledgers={ledgers} value={simpleOtherLedgerId} onChange={setSimpleOtherLedgerId} placeholder="Select ledger being received against..." darkMode={darkMode} T={T} />
                      </div>
                    </>
                  )}
                </div>
                <div style={{ marginTop:14 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Amount (₹) <span style={{ color:'#ef4444' }}>*</span></div>
                  <input type="number" step="0.01" placeholder="0.00" value={simpleAmount} onChange={e=>setSimpleAmount(e.target.value)} style={{ ...is, maxWidth:220 }} />
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Ledger entries</div>
                {/* overflow:visible — LedgerSelect's dropdown panel must be able to render outside this box, e.g. for the last line in the list. */}
                <div style={{ border:`1px solid ${T.border}`, borderRadius:10, overflow:'visible' }}>
                  {lines.map((l, i) => (
                    <div key={i} style={{
                      display:'flex', gap:8, alignItems:'center', padding:'8px 12px', borderTop: i>0 ? `1px solid ${T.border}` : 'none', background: i%2===0 ? T.bg2 : T.bg3,
                      borderTopLeftRadius: i===0 ? 9 : 0, borderTopRightRadius: i===0 ? 9 : 0,
                      borderBottomLeftRadius: i===lines.length-1 ? 9 : 0, borderBottomRightRadius: i===lines.length-1 ? 9 : 0,
                    }}>
                      <LedgerSelect ledgers={ledgers} value={l.ledger_id} onChange={v=>updateLine(i,{ledger_id:v})} darkMode={darkMode} T={T} />
                      <div style={{ display:'flex', borderRadius:8, overflow:'hidden', flexShrink:0 }}>
                        <button type="button" onClick={()=>updateLine(i,{type:'dr'})} style={{ padding:'8px 12px', border:'none', cursor:'pointer', fontSize:11.5, fontWeight:700, background: l.type==='dr'?'#2563eb':T.bg3, color: l.type==='dr'?'#fff':T.text3 }}>Dr</button>
                        <button type="button" onClick={()=>updateLine(i,{type:'cr'})} style={{ padding:'8px 12px', border:'none', cursor:'pointer', fontSize:11.5, fontWeight:700, background: l.type==='cr'?'#dc2626':T.bg3, color: l.type==='cr'?'#fff':T.text3 }}>Cr</button>
                      </div>
                      <input type="number" step="0.01" placeholder="0.00" value={l.amount} onChange={e=>updateLine(i,{amount:e.target.value})}
                        style={{ ...is, width:140, textAlign:'right', flexShrink:0 }} />
                      <button type="button" onClick={()=>removeLine(i)} disabled={lines.length<=2} style={{ width:26, height:26, flexShrink:0, borderRadius:7, border:'none', background:'none', color: lines.length<=2 ? T.border2 : '#ef4444', cursor: lines.length<=2 ? 'default' : 'pointer' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin:'0 auto' }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addLine} style={{ marginTop:10, display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:9, border:`1px dashed ${T.border2}`, background:'none', fontSize:12, fontWeight:600, color:T.text2, cursor:'pointer' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Ledger Line
                </button>
              </div>
            )}

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', borderRadius:12, background: balanced ? '#f0fdf4' : '#fff7ed', border:`1px solid ${balanced?'#bbf7d0':'#fed7aa'}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {balanced ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                )}
                <span style={{ fontSize:12.5, fontWeight:700, color: balanced ? '#16a34a' : '#c2410c' }}>
                  {balanced ? `Balanced — Total Dr ${fc(totalDr)} = Total Cr ${fc(totalCr)}` : `Total Dr ${fc(totalDr)} · Total Cr ${fc(totalCr)}`}
                </span>
              </div>
              <button onClick={handleSave} disabled={saving || !balanced} style={{ padding:'9px 22px', borderRadius:9, border:'none', background: (saving||!balanced) ? '#94a3b8' : 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:700, cursor: (saving||!balanced) ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving...' : 'Save Voucher'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ flexGrow:1, background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 2px 8px rgba(15,31,69,0.04)', display:'flex', flexDirection:'column', minHeight:0 }}>
          <div style={{ padding:'14px 22px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:13, fontWeight:800, color:T.text }}>Journal — recent vouchers</div>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                {['Date','Voucher No.','Type','Narration','Amount',''].map((h,i) => (
                  <th key={h} style={{ padding:'10px 20px', textAlign: i===4?'right':'left', fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingVouchers ? (
                <tr><td colSpan={6} style={{ padding:'30px', textAlign:'center', color:T.text3, fontSize:12.5 }}>Loading...</td></tr>
              ) : vouchers.length === 0 ? (
                <tr><td colSpan={6} style={{ padding:'30px', textAlign:'center', color:T.text3, fontSize:12.5 }}>No vouchers posted yet.</td></tr>
              ) : vouchers.slice(0, 50).map((v, i) => {
                const amount = v.lines.filter(l => l.dr_amount > 0).reduce((s,l) => s + Number(l.dr_amount), 0);
                return (
                  <tr key={v.id} style={{ borderBottom:`1px solid ${T.border}`, background: i%2===0 ? T.bg2 : T.bg3 }}>
                    <td style={{ padding:'10px 20px', fontSize:12, color:T.text2, whiteSpace:'nowrap' }}>{fd(v.voucher_date)}</td>
                    <td style={{ padding:'10px 20px', fontSize:12, color:T.text2 }}>{v.voucher_number || '—'}</td>
                    <td style={{ padding:'10px 20px' }}>
                      <span style={{ fontSize:10.5, fontWeight:700, padding:'2px 8px', borderRadius:20, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', color:T.text2 }}>
                        {VOUCHER_TYPE_LABEL[v.voucher_type] || v.voucher_type}{v.linked_type ? ' · auto' : ''}
                      </span>
                    </td>
                    <td style={{ padding:'10px 20px', fontSize:12, color:T.text, maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.narration}</td>
                    <td style={{ padding:'10px 20px', textAlign:'right', fontSize:12.5, fontWeight:700, color:T.text, fontVariantNumeric:'tabular-nums' }}>{fc(amount)}</td>
                    <td style={{ padding:'10px 20px', textAlign:'right' }}>
                      {!v.linked_type && (
                        <button onClick={() => handleDelete(v)} style={{ width:24, height:24, borderRadius:6, border:'none', background:'none', color:'#ef4444', cursor:'pointer' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin:'0 auto' }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── STATEMENTS TAB ─────────────────────────────────────────────────────────
// Trial Balance / Balance Sheet are point-in-time as of `to`; Profit & Loss /
// Cash Flow are for the from–to period. Same date bar drives all four so
// switching statements never resets the period being looked at. Lives here
// (not Reports) so entering/categorizing and reading the resulting statement
// sit in one module — Reports stays the read-only summary of the OTHER
// modules (Invoices/Receipts/Tasks/CRM/Renewals), never a second copy of this.
const STATEMENTS = [
  { id: 'trial_balance', label: 'Trial Balance', endpoint: 'trial-balance' },
  { id: 'profit_loss',   label: 'Profit & Loss',  endpoint: 'profit-loss' },
  { id: 'balance_sheet', label: 'Balance Sheet',  endpoint: 'balance-sheet' },
  { id: 'cash_flow',     label: 'Cash Flow',      endpoint: 'cash-flow' },
];

function StatementsTab({ token, darkMode, T }) {
  const [statement, setStatement] = useState('trial_balance');
  const [from, setFrom] = useState(`${new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1}-04-01`);
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const stDef = STATEMENTS.find(s => s.id === statement);

  const fetch_ = () => {
    setLoading(true);
    fetch(`${BASE}/api/accounting/reports/${stDef.endpoint}?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  };

  useEffect(() => { fetch_(); }, [statement]); // eslint-disable-line

  const asOfNote = statement === 'trial_balance' || statement === 'balance_sheet';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flexGrow: 1, minHeight: 0 }}>
      <DateFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={fetch_} loading={loading} T={T} darkMode={darkMode} />
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
        <div style={{ width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {STATEMENTS.map(s => (
            <button key={s.id} onClick={() => setStatement(s.id)}
              style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: `1px solid ${statement === s.id ? 'transparent' : T.border}`, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: statement === s.id ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg2, color: statement === s.id ? '#fff' : T.text2 }}>
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading && !data ? (
            <div style={{ padding: 48, textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading…</div>
          ) : !data ? (
            <div style={{ padding: 48, textAlign: 'center', color: T.text3, fontSize: 13 }}>No data</div>
          ) : (
            <>
              {asOfNote && statement !== 'trial_balance' && <p style={{ fontSize: 11, color: T.text3, margin: '0 0 12px' }}>As of {new Date(to).toLocaleDateString('en-IN')}</p>}
              {statement === 'trial_balance' && <TrialBalanceView token={token} from={from} to={to} T={T} darkMode={darkMode} />}
              {statement === 'profit_loss'   && <ProfitLossView   data={data} T={T} darkMode={darkMode} />}
              {statement === 'balance_sheet' && <BalanceSheetView data={data} T={T} darkMode={darkMode} />}
              {statement === 'cash_flow'     && <CashFlowView     data={data} T={T} darkMode={darkMode} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Tally-style Group-wise Trial Balance — condensed to top-level Groups by
// default (closing balance only), click any Group to expand its
// sub-Groups/Ledgers, all the way down (Current Assets → Sundry Debtors →
// every client). A Configure panel toggles Opening Balance / Transactions /
// Closing Balance columns — turning on Transactions is what surfaces a
// Ledger that nets to zero by the closing date but genuinely moved money
// during the period (e.g. a client's payment passed straight through as a
// tax deposit, no Invoice involved) — those are hidden by default, same as
// Tally's own default, since a firm usually wants the settled/net picture
// first and the raw activity only when they go looking for it.
const fmtSigned = (dr, cr) => {
  const amt = dr > 0.005 ? dr : cr;
  if (amt < 0.005) return '—';
  return `${fmt(amt)} ${dr > 0.005 ? 'Dr' : 'Cr'}`;
};

function TrialBalanceConfigPanel({ config, setConfig, onClose, darkMode, T }) {
  const Toggle = ({ label, k, hint }) => (
    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', cursor: 'pointer' }}>
      <span>
        <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: 10.5, color: T.text3, marginTop: 1 }}>{hint}</span>}
      </span>
      <input type="checkbox" checked={config[k]} onChange={e => setConfig(c => ({ ...c, [k]: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, marginLeft: 12 }} />
    </label>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg2, borderRadius: 16, width: 'min(92vw,380px)', boxShadow: darkMode ? '0 24px 60px rgba(0,0,0,0.6)' : '0 24px 60px rgba(15,31,69,0.2)' }}>
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Configuration</p>
        </div>
        <div style={{ padding: '4px 22px 6px' }}>
          <Toggle label="Show Opening Balance" k="showOpening" />
          <Toggle label="Show Transactions" k="showTransactions" hint="Also reveals Ledgers with zero closing balance that still had real movement" />
          <Toggle label="Show Closing Balance" k="showClosing" />
          <Toggle label="Show zero-balance activity" k="showZero" hint="Include Ledgers/Groups that net to zero by the closing date but had Dr/Cr movement in the period" />
          <Toggle label="Expand all on open" k="expandAll" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 22px', borderTop: `1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

function TrialBalanceRow({ node, depth, expanded, toggle, config, T, darkMode }) {
  const isGroup = !!node.totals; // groups carry rolled-up .totals; ledgers don't
  const t = isGroup ? node.totals : node;
  // Group rollups name their transaction totals dr/cr; a leaf Ledger object
  // (straight from the backend) names the same figures txn_dr/txn_cr — this
  // normalizes the two so a Ledger row's own Transactions columns show its
  // real Debit/Credit instead of silently reading undefined as zero.
  const trDr = isGroup ? t.dr : t.txn_dr;
  const trCr = isGroup ? t.cr : t.txn_cr;
  const hasChildren = isGroup && (node.children.length > 0 || node.ledgers.length > 0);
  const isOpen = expanded.has(isGroup ? `g${node.id}` : `l${node.id}`);
  const nameCellStyle = { padding: '8px 10px', paddingLeft: 14 + depth * 20, fontSize: 13, fontWeight: isGroup ? 700 : 400, fontStyle: isGroup ? 'normal' : 'italic', color: isGroup ? T.text : T.text2, display: 'flex', alignItems: 'center', gap: 6, cursor: hasChildren ? 'pointer' : 'default' };
  const amtStyle = { padding: '8px 10px', fontSize: 12.5, textAlign: 'right', color: T.text2, whiteSpace: 'nowrap' };

  const row = (
    <tr style={{ borderBottom: `1px solid ${T.border}`, background: 'transparent' }}
      onMouseEnter={e => { if (hasChildren) e.currentTarget.style.background = T.bg3; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
      <td style={nameCellStyle} onClick={() => hasChildren && toggle(isGroup ? `g${node.id}` : `l${node.id}`)}>
        {hasChildren ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="3" style={{ flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}><polyline points="9 18 15 12 9 6" /></svg>
        ) : <span style={{ width: 10, flexShrink: 0 }} />}
        {node.name}
      </td>
      {config.showOpening && (
        <td style={amtStyle}>{fmtSigned(t.opening_dr, t.opening_cr)}</td>
      )}
      {config.showTransactions && (
        <>
          <td style={amtStyle}>{trDr > 0.005 ? fmt(trDr) : '—'}</td>
          <td style={amtStyle}>{trCr > 0.005 ? fmt(trCr) : '—'}</td>
        </>
      )}
      {config.showClosing && (
        config.showOpening || config.showTransactions
          ? <td style={{ ...amtStyle, fontWeight: 700, color: T.text }}>{fmtSigned(t.closing_dr, t.closing_cr)}</td>
          : <>
              <td style={{ ...amtStyle, fontWeight: 700, color: T.text }}>{t.closing_dr > 0.005 ? fmt(t.closing_dr) : ''}</td>
              <td style={{ ...amtStyle, fontWeight: 700, color: T.text }}>{t.closing_cr > 0.005 ? fmt(t.closing_cr) : ''}</td>
            </>
      )}
    </tr>
  );

  if (!isGroup || !isOpen) return row;

  return (
    <React.Fragment>
      {row}
      {node.children.map(c => <TrialBalanceRow key={`g${c.id}`} node={c} depth={depth + 1} expanded={expanded} toggle={toggle} config={config} T={T} darkMode={darkMode} />)}
      {node.ledgers.map(l => <TrialBalanceRow key={`l${l.id}`} node={l} depth={depth + 1} expanded={expanded} toggle={toggle} config={config} T={T} darkMode={darkMode} />)}
    </React.Fragment>
  );
}

function TrialBalanceView({ token, from, to, T, darkMode }) {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({ showOpening: false, showTransactions: false, showClosing: true, showZero: false, expandAll: false });

  const fetchTB = () => {
    setLoading(true);
    fetch(`${BASE}/api/accounting/reports/trial-balance?from=${from}&to=${to}&show_zero=${config.showZero}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => {
        setRaw(d);
        if (config.expandAll) {
          const all = new Set();
          const walk = (g) => { all.add(`g${g.id}`); g.children.forEach(walk); g.ledgers.forEach(l => all.add(`l${l.id}`)); };
          (d.groups || []).forEach(walk);
          setExpanded(all);
        }
      })
      .catch(() => setRaw(null)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchTB(); }, [from, to, config.showZero, config.expandAll]); // eslint-disable-line

  const toggle = (key) => setExpanded(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });

  return (
    <Section title="Trial Balance" subtitle={raw ? `${raw.groups?.length || 0} top-level groups` : ''} T={T} darkMode={darkMode}
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {raw && !raw.balanced && <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', background: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2', padding: '4px 10px', borderRadius: 20 }}>Out of balance</span>}
          <button onClick={() => setShowConfig(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>
            Configure
          </button>
        </div>
      }>
      {showConfig && <TrialBalanceConfigPanel config={config} setConfig={setConfig} onClose={() => setShowConfig(false)} darkMode={darkMode} T={T} />}
      {(loading && !raw) ? (
        <p style={{ fontSize: 13, color: T.text3, textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : !raw || !raw.groups?.length ? (
        <p style={{ fontSize: 13, color: T.text3, textAlign: 'center', padding: 40 }}>No ledger activity yet</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  <th rowSpan={2} style={{ padding: '10px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', verticalAlign: 'bottom' }}>Particulars</th>
                  {config.showOpening && <th rowSpan={2} style={{ padding: '10px', textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', verticalAlign: 'bottom' }}>Opening Balance</th>}
                  {config.showTransactions && <th colSpan={2} style={{ padding: '6px 10px', textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transactions</th>}
                  {config.showClosing && <th rowSpan={2} colSpan={config.showOpening || config.showTransactions ? 1 : 2} style={{ padding: '10px', textAlign: config.showOpening || config.showTransactions ? 'right' : 'center', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', verticalAlign: 'bottom' }}>Closing Balance</th>}
                </tr>
                <tr>
                  {config.showTransactions && <>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>Debit</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>Credit</th>
                  </>}
                  {config.showClosing && !(config.showOpening || config.showTransactions) && <>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>Debit</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>Credit</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {raw.groups.map(g => <TrialBalanceRow key={`g${g.id}`} node={g} depth={0} expanded={expanded} toggle={toggle} config={config} T={T} darkMode={darkMode} />)}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `2px solid ${T.border}` }}>
                  <td style={{ padding: '12px 10px', fontSize: 13, fontWeight: 800, color: T.text }}>Grand Total</td>
                  {config.showOpening && <td />}
                  {config.showTransactions && <><td /><td /></>}
                  {config.showClosing && (
                    config.showOpening || config.showTransactions ? <td /> : (
                      <>
                        <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: 13.5, fontWeight: 800, color: T.text }}>{fmt(raw.total_dr)}</td>
                        <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: 13.5, fontWeight: 800, color: T.text }}>{fmt(raw.total_cr)}</td>
                      </>
                    )
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
          <p style={{ fontSize: 10.5, color: T.text3, marginTop: 10 }}>Click any bold Group row to expand it.</p>
        </>
      )}
    </Section>
  );
}

function ProfitLossView({ data, T, darkMode }) {
  const row = (label, amount, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13, fontWeight: bold ? 700 : 400, color: bold ? T.text : T.text2 }}>
      <span>{label}</span><span>{fmt(amount)}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <StatCard label="Total Income" value={fmt(data.total_income)} color="#16a34a" darkMode={darkMode} T={T}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>} />
        <StatCard label="Total Expenses" value={fmt(data.total_expenses)} color="#dc2626" darkMode={darkMode} T={T}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/></svg>} />
        <StatCard label="Net Profit" value={fmt(data.net_profit)} color={data.net_profit >= 0 ? '#16a34a' : '#dc2626'} darkMode={darkMode} T={T}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={data.net_profit >= 0 ? '#16a34a' : '#dc2626'} strokeWidth="2"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} />
      </div>
      <Section title="Income" subtitle={`${data.income?.length || 0} income ledgers`} T={T} darkMode={darkMode}>
        {(data.income || []).length === 0 ? <p style={{ fontSize: 13, color: T.text3, textAlign: 'center', padding: 24 }}>No income posted in this period</p> :
          <>{data.income.map((r, i) => <React.Fragment key={i}>{row(r.ledger_name, r.amount)}</React.Fragment>)}{row('Total Income', data.total_income, true)}</>}
      </Section>
      <Section title="Expenses" subtitle={`${data.expenses?.length || 0} expense ledgers`} T={T} darkMode={darkMode}>
        {(data.expenses || []).length === 0 ? <p style={{ fontSize: 13, color: T.text3, textAlign: 'center', padding: 24 }}>No expenses posted in this period</p> :
          <>{data.expenses.map((r, i) => <React.Fragment key={i}>{row(r.ledger_name, r.amount)}</React.Fragment>)}{row('Total Expenses', data.total_expenses, true)}</>}
      </Section>
    </div>
  );
}

function BalanceSheetView({ data, T, darkMode }) {
  const row = (label, amount, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13, fontWeight: bold ? 700 : 400, color: bold ? T.text : T.text2 }}>
      <span>{label}</span><span>{fmt(amount)}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!data.balanced && (
        <div style={{ padding: '10px 16px', borderRadius: 10, background: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 600 }}>
          Assets and Liabilities+Capital don't balance — usually means an opening balance was entered on one ledger (e.g. a bank account) without a matching Capital Account entry.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Section title="Assets" subtitle={`As of ${new Date(data.as_of).toLocaleDateString('en-IN')}`} T={T} darkMode={darkMode}>
          {(data.assets || []).length === 0 ? <p style={{ fontSize: 13, color: T.text3, textAlign: 'center', padding: 24 }}>No assets recorded</p> :
            <>{data.assets.map((r, i) => <React.Fragment key={i}>{row(r.ledger_name, r.amount)}</React.Fragment>)}{row('Total Assets', data.total_assets, true)}</>}
        </Section>
        <Section title="Liabilities & Capital" subtitle={`As of ${new Date(data.as_of).toLocaleDateString('en-IN')}`} T={T} darkMode={darkMode}>
          {(data.liabilities || []).map((r, i) => <React.Fragment key={i}>{row(r.ledger_name, r.amount)}</React.Fragment>)}
          {row('Reserves & Surplus (Net Profit this FY)', data.reserves_and_surplus)}
          {row('Total Liabilities & Capital', data.total_liabilities_and_capital, true)}
        </Section>
      </div>
    </div>
  );
}

function CashFlowView({ data, T, darkMode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <StatCard label="Operating Activities" value={fmt(data.operating_activities)} color="#3b82f6" darkMode={darkMode} T={T}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>} />
        <StatCard label="Investing Activities" value={fmt(data.investing_activities)} color="#8b5cf6" darkMode={darkMode} T={T}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} />
        <StatCard label="Financing Activities" value={fmt(data.financing_activities)} color="#f59e0b" darkMode={darkMode} T={T}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/></svg>} />
      </div>
      <Section title="Cash Position" T={T} darkMode={darkMode}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
          <div><p style={{ fontSize: 11, color: T.text3, margin: '0 0 4px' }}>Opening Cash</p><p style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>{fmt(data.opening_cash)}</p></div>
          <div><p style={{ fontSize: 11, color: T.text3, margin: '0 0 4px' }}>Net Change</p><p style={{ fontSize: 18, fontWeight: 800, color: data.net_change_in_cash >= 0 ? '#16a34a' : '#dc2626', margin: 0 }}>{fmt(data.net_change_in_cash)}</p></div>
          <div><p style={{ fontSize: 11, color: T.text3, margin: '0 0 4px' }}>Closing Cash</p><p style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>{fmt(data.closing_cash)}</p></div>
        </div>
      </Section>
    </div>
  );
}

// ─── RATIOS TAB ─────────────────────────────────────────────────────────────
// Derived entirely from the same Balance Sheet + P&L endpoints Statements
// uses — no separate data source, so a ratio here can never drift from what
// Statements shows for the same period.
const CURRENT_ASSET_GROUPS = ['Current Assets', 'Bank Accounts', 'Cash-in-Hand', 'Sundry Debtors'];
const QUICK_ASSET_GROUPS = ['Bank Accounts', 'Cash-in-Hand', 'Sundry Debtors'];
const CURRENT_LIABILITY_GROUPS = ['Current Liabilities'];
const DEBT_GROUPS = ['Loans (Liability)'];
const EQUITY_GROUPS = ['Capital Account'];

function RatioTile({ label, value, verdict, hint, T, darkMode }) {
  const colors = { good: '#16a34a', ok: '#d97706', bad: '#dc2626' };
  const color = colors[verdict] || T.text3;
  return (
    <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '18px 20px', boxShadow: darkMode ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
      </div>
      <p style={{ fontSize: 26, fontWeight: 800, color: T.text, margin: '0 0 6px' }}>{value}</p>
      <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{hint}</p>
    </div>
  );
}

function RatiosTab({ token, darkMode, T }) {
  const [to, setTo] = useState(today());
  const [from, setFrom] = useState(monthStart());
  const [bs, setBs] = useState(null);
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = () => {
    setLoading(true);
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${BASE}/api/accounting/reports/balance-sheet?from=${from}&to=${to}`, { headers: h }).then(r => r.json()),
      fetch(`${BASE}/api/accounting/reports/profit-loss?from=${from}&to=${to}`, { headers: h }).then(r => r.json()),
    ]).then(([bsRes, plRes]) => { setBs(bsRes); setPl(plRes); }).catch(() => { setBs(null); setPl(null); }).finally(() => setLoading(false));
  };

  useEffect(() => { fetch_(); }, []); // eslint-disable-line

  if (loading && !bs) return <div style={{ padding: 48, textAlign: 'center', color: T.text3, fontSize: 13 }}>Loading…</div>;
  if (!bs || !pl) return <div style={{ padding: 48, textAlign: 'center', color: T.text3, fontSize: 13 }}>No data</div>;

  const sumBy = (rows, groups) => (rows || []).filter(r => groups.includes(r.group_name)).reduce((s, r) => s + r.amount, 0);
  const currentAssets = sumBy(bs.assets, CURRENT_ASSET_GROUPS);
  const quickAssets = sumBy(bs.assets, QUICK_ASSET_GROUPS);
  const currentLiabilities = sumBy(bs.liabilities, CURRENT_LIABILITY_GROUPS);
  const debt = sumBy(bs.liabilities, DEBT_GROUPS);
  const equity = sumBy(bs.liabilities, EQUITY_GROUPS) + Number(bs.reserves_and_surplus || 0);

  const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : null;
  const quickRatio = currentLiabilities > 0 ? quickAssets / currentLiabilities : null;
  const debtToEquity = equity > 0 ? debt / equity : null;
  const npMargin = Number(pl.total_income) > 0 ? (Number(pl.net_profit) / Number(pl.total_income)) * 100 : null;

  const verdictRatio = (v, goodMin, okMin) => v == null ? null : v >= goodMin ? 'good' : v >= okMin ? 'ok' : 'bad';
  const verdictDebt = (v) => v == null ? null : v <= 1 ? 'good' : v <= 2 ? 'ok' : 'bad';
  const verdictMargin = (v) => v == null ? null : v >= 15 ? 'good' : v >= 0 ? 'ok' : 'bad';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DateFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={fetch_} loading={loading} T={T} darkMode={darkMode} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <RatioTile label="Current Ratio" T={T} darkMode={darkMode}
          value={currentRatio == null ? '—' : `${currentRatio.toFixed(2)}x`}
          verdict={verdictRatio(currentRatio, 1.5, 1)}
          hint={currentRatio == null ? 'No current liabilities recorded' : 'Current Assets ÷ Current Liabilities — ability to cover near-term obligations'} />
        <RatioTile label="Quick Ratio" T={T} darkMode={darkMode}
          value={quickRatio == null ? '—' : `${quickRatio.toFixed(2)}x`}
          verdict={verdictRatio(quickRatio, 1, 0.75)}
          hint={quickRatio == null ? 'No current liabilities recorded' : 'Bank + Cash + Debtors ÷ Current Liabilities — cash-equivalent coverage'} />
        <RatioTile label="Debt-to-Equity" T={T} darkMode={darkMode}
          value={debtToEquity == null ? '—' : `${debtToEquity.toFixed(2)}x`}
          verdict={verdictDebt(debtToEquity)}
          hint={debtToEquity == null ? 'No Capital Account balance recorded' : 'Loans ÷ (Capital + Reserves) — leverage on borrowed funds'} />
        <RatioTile label="Net Profit Margin" T={T} darkMode={darkMode}
          value={npMargin == null ? '—' : `${npMargin.toFixed(1)}%`}
          verdict={verdictMargin(npMargin)}
          hint={npMargin == null ? 'No income posted in this period' : 'Net Profit ÷ Total Income for the selected period'} />
      </div>
      <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>
        Current Ratio &amp; Quick Ratio use the Balance Sheet as of {new Date(to).toLocaleDateString('en-IN')}. Net Profit Margin uses the Profit &amp; Loss for {new Date(from).toLocaleDateString('en-IN')} – {new Date(to).toLocaleDateString('en-IN')}.
      </p>
    </div>
  );
}

export default function Accounting({ darkMode = false, T: theme }) {
  const T = theme || { bg:'#f4f7ff', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('coa');

  return (
    <div style={{ padding:24, minHeight:'100%', display:'flex', flexDirection:'column', background:T.bg }}>
      <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:24, background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:6, width:'fit-content', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)' }}>
        {[
          { key:'coa',        label:'Chart of Accounts' },
          { key:'vouchers',   label:'Vouchers' },
          { key:'bank',       label:'Bank Statements' },
          { key:'statements', label:'Statements' },
          { key:'ratios',     label:'Ratios' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:12,
            fontSize:13, fontWeight:600, cursor:'pointer', border:'none', transition:'all 0.15s',
            background: activeTab===tab.key ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
            color: activeTab===tab.key ? '#fff' : T.text2,
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'coa' && <ChartOfAccountsTab token={token} darkMode={darkMode} T={T} />}
      {activeTab === 'vouchers' && <VouchersTab token={token} darkMode={darkMode} T={T} />}
      {activeTab === 'bank' && <BankStatementsTab token={token} darkMode={darkMode} T={T} />}
      {activeTab === 'statements' && <StatementsTab token={token} darkMode={darkMode} T={T} />}
      {activeTab === 'ratios' && <RatiosTab token={token} darkMode={darkMode} T={T} />}
    </div>
  );
}
